<template>
  <div id="app" class="min-h-screen bg-bg-light">
    <!-- Global Header - Show for all users except login page -->
    <HappyAlienNav
      v-if="!isLoginPage"
      product-name="Dashboard"
      product-slug="dashboard"
      :is-authenticated="authStore.isAuthenticated"
      :user="currentUser"
      :spark-balance="currentBalance"
      :api-url="apiUrl"
      :dashboard-url="dashboardUrl"
      @logout="handleLogout"
    />

    <!-- Landing Page for Unauthenticated Users -->
    <div v-if="!authStore.isAuthenticated && !isPublicPage && authStore.isInitialized" class="min-h-screen pt-16">
      <LandingPage
        @login="router.push({ name: 'Login' })"
        @signup="router.push({ name: 'Login', query: { mode: 'signup' } })"
      />
    </div>

    <!-- Authenticated Layout with Sidebar -->
    <main v-else-if="authStore.isAuthenticated && !isLoginPage" class="pt-16">
      <div class="flex h-[calc(100vh-4rem)]">
        <SideNav :is-admin="authStore.isAdmin" @update:collapsed="sidebarCollapsed = $event" />
        <div
          class="flex-1 overflow-y-auto transition-all duration-300"
          :class="sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'"
        >
          <router-view />
        </div>
      </div>
    </main>

    <!-- Unauthenticated / Login / Public pages (no sidebar) -->
    <main v-else :class="{ 'pt-0': isLoginPage, 'pt-16': !isLoginPage }">
      <router-view />
    </main>

    <!-- Chatbot Widget -->
    <ChatbotWidget />

    <!-- Global Notifications -->
    <div v-if="notification" class="fixed top-20 right-4 z-50 max-w-sm">
      <div
        class="rounded-2xl p-4 bg-white border shadow-lg"
        :class="notificationClasses"
      >
        <div class="flex items-start space-x-3">
          <div class="flex-shrink-0">
            <PhCheckCircle
              v-if="notification.type === 'success'"
              class="w-5 h-5 text-neon-green"
              weight="fill"
            />
            <PhWarning
              v-else-if="notification.type === 'warning'"
              class="w-5 h-5 text-spark"
              weight="fill"
            />
            <PhXCircle
              v-else-if="notification.type === 'error'"
              class="w-5 h-5 text-neon-pink"
              weight="fill"
            />
            <PhInfo
              v-else
              class="w-5 h-5 text-neon-blue"
              weight="fill"
            />
          </div>

          <div class="flex-1">
            <p class="text-sm font-medium text-gray-900">
              {{ notification.title }}
            </p>
            <p
              v-if="notification.message"
              class="text-sm mt-1 text-gray-600"
            >
              {{ notification.message }}
            </p>
          </div>

          <button
            @click="clearNotification"
            class="flex-shrink-0 hover:opacity-70 transition-opacity text-gray-500"
          >
            <PhX class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  PhCheckCircle,
  PhWarning,
  PhXCircle,
  PhInfo,
  PhX,
} from "@phosphor-icons/vue";
import { HappyAlienNav } from "@onenterframe/nav-vue";
import "@onenterframe/nav-vue/style.css";
import LandingPage from "./components/LandingPage.vue";
import ChatbotWidget from "./components/ChatbotWidget.vue";
import SideNav from "./components/SideNav.vue";
import { useSparksStore } from "./stores/sparks";
import { useAuthStore } from "./stores/auth";
import { useNotificationStore } from "./stores/notifications";

const route = useRoute();
const router = useRouter();
const sparksStore = useSparksStore();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();

// Sidebar state
const sidebarCollapsed = ref(false);

// Provide stores to child components
provide("authStore", authStore);
provide("sparksStore", sparksStore);
provide("notificationStore", notificationStore);

// Computed properties
const isLoginPage = computed(() =>
  route.name === "Login" ||
  route.name === "Join" ||
  route.name === "ForgotPassword" ||
  route.name === "ResetPassword"
);
const isPublicPage = computed(() =>
  isLoginPage.value ||
  route.name === "SparkCalculator"
);
const currentUser = computed(() => authStore.user || undefined);
const currentBalance = computed(() => sparksStore.currentBalance);
const notification = computed(() => notificationStore.currentNotification);

// API URL for nav products
const apiUrl = computed(() => (import.meta as any).env.VITE_API_URL || "http://localhost:3001");

// Dashboard URL - use current origin for local dev, production URL otherwise
const dashboardUrl = computed(() => {
  if (typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return window.location.origin;
  }
  return 'https://sparks.happyalien.ai';
});

// Notification border color based on type
const notificationClasses = computed(() => {
  const typeClasses: Record<string, string> = {
    success: 'border-green-200',
    error: 'border-red-200',
    warning: 'border-yellow-200',
    info: 'border-blue-200',
  };
  return typeClasses[notification.value?.type || 'info'];
});

// Methods
function handleLogout() {
  authStore.logout();
  sparksStore.clearData();
  clearNotification();
  router.push({ name: "Login" });
}

function clearNotification() {
  notificationStore.clear();
}

// Initialize app
onMounted(async () => {
  console.log("[App] mounted, initializing auth...");

  // Initialize authentication
  await authStore.initialize();

  console.log("[Auth] initialized:", {
    isAuthenticated: authStore.isAuthenticated,
    hasUser: !!authStore.user,
    hasToken: !!authStore.accessToken,
    userId: authStore.user?.id
  });

  if (authStore.isAuthenticated && !sparksStore.isInitialized) {
    // Initialize sparks store with direct API calls
    const apiUrl = (import.meta as any).env.VITE_API_URL || "http://localhost:3001";
    console.log("[API] Connecting to:", apiUrl);

    // Initialize stores without SDK
    sparksStore.initializeWithAPI(apiUrl, authStore.accessToken);

    // Load initial dashboard data
    console.log("[Dashboard] Loading initial data...");

    try {
      await sparksStore.refreshDashboard();
      console.log("[Dashboard] Data loaded successfully");

      // Now start auto-refresh for balance updates
      sparksStore.startAutoRefresh();
    } catch (error) {
      console.error("[Dashboard] Failed to load data:", error);
      // Still start auto-refresh even if initial load fails
      sparksStore.startAutoRefresh();
    }
  } else {
    console.log("[Sparks] Not initializing:", {
      isAuthenticated: authStore.isAuthenticated,
      sparksInitialized: sparksStore.isInitialized
    });
  }
});
</script>
